export const isEmptyValue = (val) => val == null || val === "";

/**
 * Returns the first object from an array where a property matches a specified value.
 *
 * @param {Array<Object>} array - Array of objects to search.
 * @param {string} property - The property name to match (top-level only).
 * @param {*} value - The value to match.
 * @returns {Object|null} The found object or null if no match is found.
 *
 * @example
 * // Given:
 * const roles = [
 *   { ROLECODE: "DEV", ROLEDESC: "Developer", MODULE: { ... } },
 *   { ROLECODE: "USER", ROLEDESC: "User", MODULE: { ... } }
 * ];
 * // Usage:
 * findObjectByProperty(roles, "ROLECODE", "DEV");
 * // Returns:
 * // { ROLECODE: "DEV", ROLEDESC: "Developer", MODULE: { ... } }
 */

/**
 * Returns the first object in an array where the property at propertyPath equals value.
 * Supports dot notation for nested keys, e.g. "meta.color".
 *
 * @param {Array} array - Array to search.
 * @param {string} propertyPath - Dot-notated property path to match.
 * @param {*} value - Value to match.
 * @returns {Object|null} The first matching object or null if not found.
 *
 * @example
 * const arr = [
 *   { id: 1, meta: { color: "red" } },
 *   { id: 2, meta: { color: "yellow" } }
 * ];
 * findObjectByProperty(arr, "id", 2);
 * // => { id: 2, meta: { color: "yellow" } }
 *
 * findObjectByProperty(arr, "meta.color", "red");
 * // => { id: 1, meta: { color: "red" } }
 */
export const findObjectByProperty = (array, propertyPath, value) => {
	if (!Array.isArray(array)) return null;
	const keys = propertyPath.split(".");
	for (const obj of array) {
		let current = obj;
		for (const key of keys) {
			if (!current || typeof current !== "object" || !(key in current)) {
				current = undefined;
				break;
			}
			current = current[key];
		}
		if (current === value) return obj;
	}
	return null;
};

/**
 * Returns an array of objects from the input array where a given property matches a specific value.
 *
 * @param {Array<Object>} arr - The array of objects to search.
 * @param {string} property - The property name to match (top-level only).
 * @param {*} value - The value to match.
 * @returns {Array<Object>} Array of matched objects (may be empty).
 *
 * @example
 * // Given:
 * const roles = [
 *   { ROLECODE: "DEV", ROLEDESC: "Developer", MODULE: { ... } },
 *   { ROLECODE: "USER", ROLEDESC: "User", MODULE: { ... } }
 * ];
 * // Usage:
 * getObjArrayByKey(roles, "ROLECODE", "DEV");
 * // Returns:
 * // [{ ROLECODE: "DEV", ROLEDESC: "Developer", MODULE: { ... } }]
 */

/**
 * Returns an array of objects from array where the nested property path equals value.
 * Supports dot notation for nested keys, e.g. "a.b.c".
 *
 * @param {Array} array - Array to search.
 * @param {string} propertyPath - Dot-notated property path to match.
 * @param {*} value - Value to match.
 * @returns {Array} Array of matching objects (empty if none).
 *
 * @example
 * const array = [
 *   { id: 1, meta: { type: "fruit", color: "red" } },
 *   { id: 2, meta: { type: "fruit", color: "yellow" } },
 *   { id: 3, meta: { type: "vegetable", color: "red" } }
 * ];
 * // Returns all red items
 * filterObjectsByProperty(array, "meta.color", "red");
 * // => [
 * //   { id: 1, meta: { type: "fruit", color: "red" } },
 * //   { id: 3, meta: { type: "vegetable", color: "red" } }
 * // ]
 */
export const filterObjectsByProperty = (array, propertyPath, value) => {
	if (!Array.isArray(array)) return [];
	const keys = propertyPath.split(".");
	return array.filter((obj) => {
		let current = obj;
		for (const key of keys) {
			if (!current || typeof current !== "object" || !(key in current)) return false;
			current = current[key];
		}
		return current === value;
	});
};

/**
 * Returns an array of property values from an array of objects.
 *
 * @param {Array<Object>} arr - The array of objects to extract from.
 * @param {string} property - The property name to extract.
 * @returns {Array<*>} Array of property values (may be empty).
 *
 * @example
 * // Given:
 * const roles = [
 *   { ROLECODE: "DEV", ROLEDESC: "Developer" },
 *   { ROLECODE: "SADMN", ROLEDESC: "Admin" }
 * ];
 * // Usage:
 * getPropertyFromObjArray(roles, "ROLECODE");
 * // Returns:
 * // ["DEV", "SADMN"]
 */

/**
 * Returns an array of values for a property (supports nested keys) from each object in the array.
 *
 * @param {Array} array - The array of objects.
 * @param {string} propertyPath - The key (or dot-notated nested key) to extract.
 * @returns {Array} Array of property values (empty array if arr is falsy).
 *
 * @example
 * const array = [
 *   { id: 1, meta: { value: "foo" } },
 *   { id: 2, meta: { value: "bar" } }
 * ];
 * getValuesByPropertyPath(array, "id");
 * // => [1, 2]
 * getValuesByPropertyPath(array, "meta.value");
 * // => ["foo", "bar"]
 */
export const getValuesByPropertyPath = (array, propertyPath) => {
	if (!Array.isArray(array)) return [];
	const keys = propertyPath.split(".");
	return array.map((obj) => {
		let current = obj;
		for (const key of keys) {
			if (!current || typeof current !== "object" || !(key in current)) return undefined;
			current = current[key];
		}
		return current;
	});
};

/**
 * Returns a property's value from the first object in an array where another property matches a given value.
 *
 * @param {Array<Object>} array - The array of objects to search.
 * @param {string} key - The property name to match.
 * @param {*} value - The value to match against `key`.
 * @param {string} targetProperty - The property name whose value should be returned.
 * @returns {*} The value of `targetProperty` if a match is found, otherwise an empty string.
 *
 * @example
 * // Given:
 * const roles = [
 *   { ROLECODE: "DEV", ROLEDESC: "Developer" },
 *   { ROLECODE: "SADMN", ROLEDESC: "Admin" }
 * ];
 * // Usage:
 * getPropertyByKey(roles, "ROLECODE", "DEV", "ROLEDESC");
 * // Returns: "Developer"
 */

/**
 * Returns the value of a nested property (`targetProperty`) from the first object in the array
 * where a (possibly nested) `key` matches `value`.
 *
 * @param {Array<Object>} arrayObj - Array of objects to search.
 * @param {string} key - Property path to match (e.g., "a.b.c").
 * @param {*} value - Value to match.
 * @param {string} targetProperty - Property path to return (e.g., "x.y.z").
 * @returns {*} The value of targetProperty from the matched object, or "" if not found.
 *
 * @example
 * const arrayObj = [
 *   { id: 1, data: { score: 10 } },
 *   { id: 2, data: { score: 20 } }
 * ];
 * // Returns 20
 * getPropertyByKey(arrayObj, "id", 2, "data.score");
 */
export const getPropertyByKey = (arrayObj, key, value, targetProperty) => {
	if (!arrayObj) return "";
	const getNested = (obj, path) => path.split(".").reduce((acc, k) => acc && acc[k], obj);

	for (let i = 0; i < arrayObj.length; i++) {
		if (getNested(arrayObj[i], key) === value) {
			const found = getNested(arrayObj[i], targetProperty);
			return found === undefined ? "" : found;
		}
	}
	return "";
};

/*
 * Returns the nested value if found, or defaultValue if any level is missing/null/undefined.
 * const myObj = {
 *   a: { b: { c: [1, 2, 3] } },
 * };
 *
 * getDeepValue(myObj, "a.b.c", []);        // [1,2,3]
 * getDeepValue(myObj, "a.b.d", []);        // []  (since 'd' missing)
 * getDeepValue(myObj, "a.b.c.0", 0);       // 1
 * getDeepValue(myObj, "a.x.c");        // {} (since 'x' missing)
 * getDeepValue(null, "a.b", []);           // []
 *
 */
export const getDeepValue = (obj, path, defaultValue = {}) => {
	if (!obj || typeof path !== "string") return defaultValue;
	const result = path
		.split(".")
		.reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), obj);
	return result !== undefined ? result : defaultValue;
};

/**
 * Returns a new array with unique objects from the input array,
 * using the specified property as the unique key.
 * Keeps the **last** occurrence of each unique key.
 *
 * @param {Array<Object>} arr - The array of objects to filter.
 * @param {string} key - The property name to determine uniqueness.
 * @returns {Array<Object>} Array of unique objects by the specified key.
 *
 * @example
 * const input = [
 *   { id: 1, name: "A" },
 *   { id: 2, name: "B" },
 *   { id: 1, name: "C" }
 * ];
 * uniqueByKey(input, "id");
 * // Returns: [
 * //   { id: 2, name: "B" },
 * //   { id: 1, name: "C" }
 * // ]
 */
export function uniqueByKey(arr, key) {
	if (!Array.isArray(arr)) return [];
	const map = new Map();
	arr.forEach((item) => {
		if (item && Object.prototype.hasOwnProperty.call(item, key)) {
			map.set(item[key], item);
		}
	});
	return Array.from(map.values());
}

export function dynamicSort(property) {
	var sortOrder = 1;
	if (property[0] === "-") {
		sortOrder = -1;
		property = property.substr(1);
	}
	return function (a, b) {
		/*
		 * next line works with strings and numbers,
		 * and you may want to customize it to your needs
		 */
		var result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
		return result * sortOrder;
	};
}

function descendingComparator(a, b, orderBy) {
	// if (b[orderBy] < a[orderBy]) {
	// 	return -1;
	// }
	// if (b[orderBy] > a[orderBy]) {
	// 	return 1;
	// }
	// return 0;

	const aVal = a[orderBy];
	const bVal = b[orderBy];

	// Push nullish or empty values to the bottom
	// const aEmpty = aVal === null || aVal === undefined || aVal === "";
	// const bEmpty = bVal === null || bVal === undefined || bVal === "";
	const aEmpty = isEmptyValue(aVal);
	const bEmpty = isEmptyValue(bVal);

	if (aEmpty && !bEmpty) return 1;
	if (!aEmpty && bEmpty) return -1;
	if (aEmpty && bEmpty) return 0;

	if (bVal < aVal) return -1;
	if (bVal > aVal) return 1;
	return 0;
}

export function getComparator(order, orderBy) {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
export function stableSort(array, comparator) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

// check and see if all elements in the array are the same
export const areAllElementsSame = (arr) => {
	return arr.reduce((acc, curr) => acc && curr === arr[0], true);
};

/**
 * Deeply clones objects and arrays (no functions/circular refs).
 * @param {any} obj - Any JSON-safe JS object
 * @param {any} [emptyFallback] - Optional fallback returned when obj is an empty object/array.
 * @returns {any} - Deep copy of obj
 * spread ({ ...baseline }) only shallow clones the top level, and nested arrays/objects
 * stay as references, and if they’re frozen or non-extensible, you’ll get
 * “object is not extensible” errors when mutating.
 *
 * deepClone gives you a fresh, deeply-copied object (and arrays) you can mutate safely.
 *
 * No more TypeError: You can now map, push, assign new keys, etc., inside your processing logic.
 */
export function deepClone(obj, emptyFallback) {
	/*	
		// this also works
		if (obj === null || typeof obj !== "object") return obj;
		if (Array.isArray(obj)) {
			return obj.map(deepClone);
		}
		const result = {};
		for (const key in obj) {
			// Only clone own properties
			if (Object.prototype.hasOwnProperty.call(obj, key)) {
				result[key] = deepClone(obj[key]);
			}
		}
		return result;
	*/
	/*
		// Modern browsers support structuredClone (handles Dates, etc.)
		if (typeof structuredClone === "function") {
			return structuredClone(obj);
		}
		// Fallback: JSON method (doesn't handle Dates/Functions, but works for plain data)
		return JSON.parse(JSON.stringify(obj));
	*/

	// Nullish or empty object → treat as null
	if (obj === null || obj === undefined) return null;

	// Treat completely empty objects and arrays as null by default (legacy behavior).
	// If emptyFallback is provided, return that instead.
	if (typeof obj === "object" && Object.keys(obj).length === 0) {
		if (arguments.length >= 2) return emptyFallback;
		return null;
	}

	// Prefer modern deep clone
	if (typeof structuredClone === "function") {
		return structuredClone(obj);
	}

	// JSON fallback for legacy environments
	return JSON.parse(JSON.stringify(obj));
}

/**
 * Generates a pseudo-random GUID/UUID v4-like string.
 *
 * This function creates a 32-character hexadecimal string in the format XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXX,
 * useful for unique identifiers in applications where cryptographic strength is not required.
 *
 * @returns {string} A pseudo-random GUID string.
 *
 * @example
 * const id = guid();
 * // 'fc97c4d0-86ef-c7fa-55e5-2bcd8ab2f38b'
 */
export const guid = () => {
	/**
	 * Helper to produce a random 8-character hex segment.
	 * @param {boolean} [addSeparator=false] - Whether to prepend hyphens to the segment.
	 * @returns {string}
	 */
	function _p8(addSeparator = false) {
		const p = (Math.random().toString(16) + "000000000").substr(2, 8);
		return addSeparator ? "-" + p.substr(0, 4) + "-" + p.substr(4, 4) : p;
	}
	return _p8() + _p8(true) + _p8(true) + _p8();
};

/**
 * Checks whether a value is a valid UUID/GUID (version-agnostic).
 * Supports both lowercase and uppercase hexadecimal.
 *
 * @param {*} uuid - The value to check (string or any).
 * @returns {boolean} True if the input is a valid UUID; otherwise, false.
 *
 * @example
 * isUUID('123e4567-e89b-12d3-a456-426614174000'); // true
 * isUUID('not-a-uuid'); // false
 */

export const isUUID = (uuid) => {
	if (typeof uuid !== "string") return false;
	const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
	return regex.test(uuid);
};

/**
 * Generates a random integer between the given min and max values, inclusive.
 *
 * @param {number} [min=1000000000] - The lower bound (inclusive).
 * @param {number} [max=2000000000] - The upper bound (inclusive).
 * @returns {number} A random integer between min and max.
 *
 * @example
 * getRandomInteger();           // Random int between 1000000000 and 2000000000
 * getRandomInteger(1, 10);      // Random int between 1 and 10
 * getRandomInteger(100, 1000);  // Random int between 100 and 1000
 */
export function getRandomInteger(min = 1000000000, max = 2000000000) {
	if (typeof min !== "number" || typeof max !== "number" || min > max) {
		throw new Error("Invalid arguments: min and max must be numbers and min <= max.");
	}
	return Math.floor(min + Math.random() * (max - min + 1));
}

/**
 * Returns the last index in the array where the value is a non-zero number.
 *
 * Converts each element to a number (using * 1), skips `NaN` and zero values,
 * and returns the index of the last such occurrence. If not found, returns -1.
 *
 * @param {Array} arr - The array to search.
 * @returns {number} The last index with a non-zero numeric value, or -1 if none found.
 *
 * @example
 * findLastNonZeroIndex([0, 0, 3, 0, -2, 0]); // 4
 * findLastNonZeroIndex([0, 0, 0]);           // -1
 * findLastNonZeroIndex(["0", "a", "5"]);     // 2
 */

export const findLastNonZeroIndex = (arr) => {
	if (!Array.isArray(arr)) return -1;
	let index = -1;
	for (let i = arr.length - 1; i >= 0; i--) {
		const v = Number(arr[i]);
		if (!isNaN(v) && v !== 0) {
			index = i;
			break;
		}
	}
	return index;
};

/**
 * Returns the index of the first non-zero, non-NaN value in an array.
 *
 * @param {Array<number|string>} arr - The array to search. Values will be coerced to numbers.
 * @returns {number} The index of the first non-zero value, or -1 if none is found.
 *
 * @example
 * findFirstNonZeroIndex([0, 0, "2", 0, 1]) // 2
 * findFirstNonZeroIndex([0, 0, 0]) // -1
 */

export const findFirstNonZeroIndex = (arr) => {
	if (!Array.isArray(arr)) return -1;
	for (let i = 0; i < arr.length; i++) {
		// <-- fixed: check all elements
		const v = Number(arr[i]);
		if (!isNaN(v) && v !== 0) {
			return i;
		}
	}
	return -1;
};

/**
 * Shift an array to the right by n positions, filling new entries with 0.
 * @param {Array} arr - The original array.
 * @param {number} n - Number of positions to shift right.
 * @returns {Array} The shifted array.
 * @example
 * shiftAndZeroLeadingRight([1, 2, 3, 4], 2) // [0, 0, 1, 2]
 */
export const shiftAndZeroLeadingRight = (arr, idx) => {
	if (!Array.isArray(arr) || typeof idx !== "number" || idx < 0) return [];
	// Create the shifted array (prepending zeros, dropping elements off the end)
	const shifted = [...new Array(idx).fill(0), ...arr].slice(0, arr.length);
	// Set the first `idx` elements to zero (even if arr had non-zero values there)
	for (let i = 0; i < idx && i < shifted.length; i++) {
		shifted[i] = 0;
	}
	return shifted;
};

/**
 * Shift an array to the right by n positions, filling new entries with 0.
 * @param {Array} arr - The original array.
 * @param {number} n - Number of positions to shift right.
 * @returns {Array} The shifted array.
 * @example
 * shiftArrayRight([1, 2, 3, 4], 2) // [0, 0, 1, 2]
 */
export const shiftArrayRight = (arr, n) => {
	if (!Array.isArray(arr) || typeof n !== "number" || n < 0) return [];
	const shifted = new Array(n).fill(0).concat(arr).slice(0, arr.length);
	return shifted;
};

/**
 * Returns a new array with its elements shifted left by one position and the last value set to 0,
 * then sets all values before the given index to 0.
 *
 * @param {Array<number>} arr - The array to shift.
 * @param {number} idx - The index before which all elements are set to 0 after shifting.
 * @returns {Array<number>} A new shifted array with appropriate zeroing.
 *
 * @example
 * // shiftAndZeroLeadingLeft([1,2,3,4], 2) returns [0,0,3,0]
 */
export const shiftAndZeroLeadingLeft = (arr, idx) => {
	if (!Array.isArray(arr) || typeof idx !== "number" || idx < 0) return [];

	// Create a shallow copy to avoid mutating the original array
	let result = arr.slice();
	result.shift(); // Remove first element
	result.push(0); // Add 0 to the end

	// Set all values before idx to 0
	for (let i = 0; i < idx && i < result.length; i++) {
		result[i] = 0;
	}

	return result;
};

/**
 * Shift an array to the left by n positions, filling new entries with 0.
 * @param {Array} arr - The original array.
 * @param {number} n - Number of positions to shift left.
 * @returns {Array} The shifted array.
 * @example
 * shiftArrayLeft([1, 2, 3, 4], 2) // [3, 4, 0, 0]
 */
export const shiftArrayLeft = (arr, n) => {
	if (!Array.isArray(arr) || typeof n !== "number" || n < 0) return [];
	const shifted = arr.slice(n).concat(new Array(n).fill(0));
	return shifted.slice(0, arr.length);
};

export const sumArrays = (a, b) => {
	var c = [];
	for (var i = 0; i < Math.max(a.length, b.length); i++) {
		c.push((a[i] * 1 || 0) + (b[i] * 1 || 0));
	}
	return c;
};

/**
 * Generic utility to merge/replace items in an array property of a parent object, 
 * DRY for configs, items, force structures, etc.
 *
 * @param {Object} parentObj - The object containing the array to update (e.g., baseline, item, etc.)
 * @param {string} arrayKey - The name of the array property to update (e.g. "baseline_configs", "baseline_item_configs")
 * @param {Array} newItems - The array of new/updated items to merge in.
 * @param {string} idKey - The key used for matching uniqueness (default: "id")
 * @param {Function} [filterFn=null] - Optional filter to apply to newItems before merge
 * @returns {Object} - A deep-cloned updated parent object.
 */
export const mergeArrayById = (parentObj, arrayKey, newItems, idKey = "id", filterFn = null) => {
	// Deep clone parent to prevent mutation
	const clone = deepClone(parentObj);

	// Optionally filter items before merging (e.g., by update_flag)
	let itemsToMerge = newItems;
	if (filterFn) itemsToMerge = itemsToMerge.filter(filterFn);

	// Collect all ids in the new/updated set
	const idsToReplace = itemsToMerge.map((x) => x[idKey]);

	// Remove all items in the existing array with matching ids
	clone[arrayKey] = (clone[arrayKey] || []).filter((x) => !idsToReplace.includes(x[idKey]));

	// Add the new/updated items
	clone[arrayKey] = [...clone[arrayKey], ...itemsToMerge];

	return clone;
};

/**
 * Merges an array inside an object by a unique key (e.g., "id"), supporting optional filtering,
 * and safe deletion of items matching a specific typeKey and typeValue.
 *
 * - Replaces items in the existing array if their `keyProp` matches an item in `newItems`.
 * - Adds new items not present in the existing array.
 * - Removes items only if:
 *   - Their `keyProp` is not found in `newItems`, AND
 *   - They match `typeKey = typeValue` (e.g., `comp_type_id = 112`)
 *
 * @param {Object} parentObj - The parent object containing the array to be updated.
 * @param {string} arrayKey - The key of the array inside the parent object (e.g., "baseline_configs").
 * @param {Array} newItems - New or updated items to merge.
 * @param {string} [keyProp="id"] - The unique property used to match and replace items (e.g., "id").
 * @param {Function} [filterFn=null] - Optional filter to pre-filter newItems (e.g., only items with update_flag !== 103).
 * @param {string|null} [typeKey=null] - Optional key to scope deletion (e.g., "comp_type_id").
 * @param {*} [typeValue=null] - Value of the `typeKey` to restrict deletions to (e.g., 112).
 * @returns {Object} A deep-cloned copy of the parent object with the updated array.
 *
 * @example
 * const updatedBaseline = mergeArrayByPropertyWithDeletes(
 *   currBaseline,
 *   "baseline_configs",
 *   updatedConfigs,
 *   "id",
 *   (item) => item.update_flag !== 103, // Only merge active items
 *   "comp_type_id",
 *   112 // Only remove configs of this type if missing
 * );
 */
export const mergeArrayByPropertyWithDeletes = (
	parentObj,
	arrayKey,
	newItems,
	keyProp = "id",
	filterFn = null,
	typeKey = null,
	typeValue = null
) => {
	const clone = deepClone(parentObj);

	// Apply optional filter to new items
	let itemsToMerge = newItems;
	if (filterFn) {
		itemsToMerge = itemsToMerge.filter(filterFn);
	}

	const allNewIds = newItems.map((x) => x[keyProp]);
	const idsToReplace = itemsToMerge.map((x) => x[keyProp]);

	// Filter out existing items to:
	// - Remove items being replaced
	// - Remove items (within same type group) not in the newItems list
	clone[arrayKey] = (clone[arrayKey] || []).filter((x) => {
		const matchKey = x[keyProp];

		// Replace if match found in merge list
		if (idsToReplace.includes(matchKey)) return false;

		// Only delete if type matches AND it's missing from incoming list
		if (typeKey && typeValue != null && x[typeKey] === typeValue && !allNewIds.includes(matchKey)) {
			return false; // Mark for deletion
		}

		return true; // Keep
	});

	// Append new/updated items
	clone[arrayKey] = [...clone[arrayKey], ...itemsToMerge];

	return clone;
};

/**
 * Normalizes a value into a trimmed string.
 * - Trims strings.
 * - Converts numbers and booleans to strings.
 * - Converts null or undefined to an empty string.
 *
 * @param {*} v - The value to normalize (string, number, boolean, null, or undefined).
 * @returns {string} The normalized, trimmed string.
 *
 * @example
 * normalizeToString("  hello  "); // "hello"
 * normalizeToString(123);         // "123"
 * normalizeToString(null);        // ""
 * normalizeToString(true);        // "true"
 */
export const normalizeToString = (v) => {
	// If the value is a string => .trim() it;
	// if not => convert it to a string (for validator)
	if (typeof v === "string") return v.trim();
	if (v == null) return ""; // null or undefined => empty string
	return String(v); // numbers, booleans => string
};

/**
 * Checks if a value exists in an array, comparing each element as a string.
 * This allows matching numbers and strings equivalently (e.g., 1 == "1").
 *
 * @param {Array} arr - The array to search.
 * @param {*} val - The value to check for existence in the array.
 * @returns {boolean} True if `val` is found in `arr`, false otherwise.
 *
 * @example
 * arrayIncludes([1, 2, 3], "2"); // true
 * arrayIncludes(["a", "b", "c"], "B"); // false (case-sensitive)
 */
export const arrayIncludes = (arr, val) => arr.some((x) => String(x) === String(val));

/**
 * Checks if the provided value is a plain JavaScript object (i.e., not null, not an array).
 *
 * @param {*} val - The value to check.
 * @returns {boolean} Returns `true` if the value is a plain object, otherwise `false`.
 *
 * @example
 * isObject({}); // true
 * isObject({ a: 1 }); // true
 * isObject([]); // false
 * isObject(null); // false
 * isObject(42); // false
 * isObject("hello"); // false
 */
export const isObject = (val) => val !== null && typeof val === "object" && !Array.isArray(val);

/**
 * Checks whether a given value is a plain object.
 *
 * A plain object is typically created using `{}` or `new Object()`,
 * and does not include arrays, functions, dates, maps, sets, class instances, etc.
 *
 * @param {*} obj - The value to check.
 * @returns {boolean} Returns `true` if the value is a plain object, otherwise `false`.
 *
 * @example
 * isPlainObject({}); // true
 * isPlainObject(new Object()); // true
 * isPlainObject(Object.create(null)); // true
 *
 * @example
 * isPlainObject([]); // false
 * isPlainObject(new Date()); // false
 * isPlainObject(null); // false
 * isPlainObject(() => {}); // false
 * isPlainObject(new Map()); // false
 * isPlainObject("text"); // false
 */
export const isPlainObject = (obj) => {
	return Object.prototype.toString.call(obj) === "[object Object]";
};

export const getIdFromPath = (pathname) => {
	const parts = pathname.split("/").filter(Boolean); // removes "" segments
	const last = parts[parts.length - 1];

	// If the URL ends exactly with "/baseline", there is NO id
	// if (last === "baseline") return null;
	// if (last === "scenario") return null;
	
	// List of modules that should NOT be treated as IDs
	const moduleRoots = ["baseline", "scenario", "user"];

	// If the last path segment is a module name → no ID present
	if (moduleRoots.includes(last)) return null;

	return last || null;
};
